"use client"

import { useState, useContext, useEffect, useRef, useMemo } from "react"
import { useParams, useLocation } from "react-router-dom"
import { ErpContext } from "../../context/ErpContext.js"
import Cookies from "js-cookie"
import { formatNumberForDisplay, formatInputWithSeparators, parseLocalizedNumber, getLocaleConfig } from "../../utils/numeros.js"

// Material UI Components
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  IconButton,
  Autocomplete,
  Divider,
  Tooltip,
  FormControlLabel,
  InputAdornment,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Snackbar,
  useTheme,
  alpha,
  Checkbox,
  Collapse,
} from "@mui/material"

// Material UI Icons
import {
  Close as CloseIcon,
  ErrorOutline as ErrorOutlineIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Delete as DeleteIcon,
  PersonAddAlt as PersonAddAltIcon,
  AddShoppingCart as AddShoppingCartIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  FlightTakeoff as FlightTakeoffIcon,
  LocationOn as LocationOnIcon,
  Group as GroupIcon,
  Luggage as LuggageIcon,
  Send as SendIcon,
} from "@mui/icons-material"

// Components
import CrearEntidadClienteModalComponent from "../adminComponent/CrearEntidadClienteModalComponent.js"
import "./GestionGenerarVentaComponent.css"

// Styled Table Components
import { styled } from "@mui/material/styles"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell, { tableCellClasses } from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TableFooter from "@mui/material/TableFooter"

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#5b5b5f",
    color: theme.palette.common.white,
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}))

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.light, 0.1),
    transition: "background-color 0.2s ease",
  },
  "& .MuiTableCell-root": {
    padding: "12px 16px",
  },
}))

/**
 * Componente para generar ventas de agencia de viajes
 * 
 * NUEVA LÓGICA:
 * 1. Seleccionar Grupo (item con esServicio=false) y Destino (ubicación)
 * 2. Agregar Pasajeros (entidades) al viaje
 * 3. Generar una transacción por cada pasajero
 */
const GestionGenerarVentaComponent = () => {
  const theme = useTheme()
  const location = useLocation()
  const { id } = useParams()

  // Context
  const {
    getItemTresLetras,
    itemTresLetras,
    arrayItemTresLetras,
    entidadesClientesTresLetras,
    getEntidadesClientesTresLetras,
    postTransaccionItem,
    postTransaccion,
    getUsuario,
    usuarios,
    getUbicacion,
    ubicaciones,
    getListaMontosByIdItemIdEntidad,
    isNavbarCollapsed,
    formatoNumericoGlobal,
  } = useContext(ErpContext)

  // Configuración de locale numérico
  const localeConfig = useMemo(
    () => getLocaleConfig(formatoNumericoGlobal || null),
    [formatoNumericoGlobal]
  )

  // Estados principales
  const [selectedGrupo, setSelectedGrupo] = useState(null) // Item grupo seleccionado
  const [selectedDestino, setSelectedDestino] = useState(null) // Destino/ubicación seleccionado
  const [selectedPasajero, setSelectedPasajero] = useState(null) // Pasajero a agregar
  const [searchTextGrupo, setSearchTextGrupo] = useState("")
  const [searchTextPasajero, setSearchTextPasajero] = useState("")
  const [pasajerosList, setPasajerosList] = useState([]) // Lista de pasajeros agregados
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null)

  // Estados para descuentos
  const [aplicarDescuento, setAplicarDescuento] = useState(false)
  const [tempValorDescuento, setTempValorDescuento] = useState("0")
  const [tempPorcentajeDescuento, setTempPorcentajeDescuento] = useState("0")

  // Estados para totales
  const [precioBaseTotal, setPrecioBaseTotal] = useState(0)
  const [precioTotal, setPrecioTotal] = useState(0)

  // Estados para modales
  const [abrirModalCrearPasajero, setAbrirModalCrearPasajero] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [pasajeroAEditar, setPasajeroAEditar] = useState(null)

  // Estados para UI
  const [loading, setLoading] = useState(false)
  const [isButtonDisabled, setIsButtonDisabled] = useState(true)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  })

  // Referencias
  const autocompleteGrupoRef = useRef(null)
  const autocompletePasajeroRef = useRef(null)

  // Función para mostrar snackbar
  const showSnackbar = (message, severity = "info") => {
    setSnackbar({
      open: true,
      message,
      severity,
    })
  }

  // Función para cerrar snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    })
  }

  // Efecto para cargar datos iniciales
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        await getUbicacion()
        await getUsuario()
        showSnackbar("Datos cargados correctamente", "success")
      } catch (error) {
        console.error("Error al cargar los datos:", error)
        showSnackbar("Error al cargar los datos iniciales", "error")
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  // Efecto para cargar usuario por defecto
  useEffect(() => {
    const idUsuarioGuardado = Cookies.get("idUsuario")
    if (idUsuarioGuardado && usuarios.length > 0) {
      const usuarioDefault = usuarios.find((user) => user.id === Number.parseInt(idUsuarioGuardado, 10))
      if (usuarioDefault) {
        setUsuarioSeleccionado({
          label: `${usuarioDefault.usuario} ${usuarioDefault.nombre || ""} ${usuarioDefault.apellido || ""}`,
          ...usuarioDefault,
        })
      }
    }
  }, [usuarios])

  // Efecto para búsqueda de grupos (items con esServicio=false)
  useEffect(() => {
    const buscarGrupo = async () => {
      if (searchTextGrupo.length >= 3) {
        const resultado = await getItemTresLetras(searchTextGrupo)
        // Filtrar solo grupos (esServicio = false)
        if (resultado && resultado.items) {
          const gruposFiltrados = resultado.items.filter(item => item.esServicio === false)
          arrayItemTresLetras(gruposFiltrados)
        }
      } else {
        arrayItemTresLetras([])
      }
    }

    buscarGrupo()
  }, [searchTextGrupo])

  // Efecto para búsqueda de pasajeros
  useEffect(() => {
    if (searchTextPasajero.length >= 3) {
      getEntidadesClientesTresLetras(searchTextPasajero, 1)
    }
  }, [searchTextPasajero])

  // Efecto para habilitar/deshabilitar botón
  useEffect(() => {
    if (pasajerosList.length > 0 && selectedGrupo && selectedDestino && usuarioSeleccionado) {
      setIsButtonDisabled(false)
    } else {
      setIsButtonDisabled(true)
    }
  }, [pasajerosList, selectedGrupo, selectedDestino, usuarioSeleccionado])

  // Efecto para calcular totales
  useEffect(() => {
    const calcularTotales = () => {
      const subtotal = pasajerosList.reduce((total, pasajero) => total + pasajero.precio, 0)
      setPrecioBaseTotal(subtotal)

      // Aplicar descuentos
      const valorDesc = Number(Number.parseFloat(tempValorDescuento).toFixed(2)) || 0
      const porcentajeDesc = Number(Number.parseFloat(tempPorcentajeDescuento).toFixed(2)) || 0

      let totalConDescuento = subtotal

      if (valorDesc > 0) {
        totalConDescuento -= valorDesc
      }

      if (porcentajeDesc > 0) {
        const porcentajeDescuentoMonto = (totalConDescuento * porcentajeDesc) / 100
        totalConDescuento -= porcentajeDescuentoMonto
      }

      if (totalConDescuento < 0) {
        totalConDescuento = 0
      }

      setPrecioTotal(totalConDescuento)
    }

    calcularTotales()
  }, [pasajerosList, tempValorDescuento, tempPorcentajeDescuento])

  // Función para agregar pasajero
  const handleAddPasajero = async () => {
    try {
      if (!selectedGrupo || !selectedDestino) {
        showSnackbar("Debe seleccionar un grupo y un destino primero", "warning")
        return
      }

      if (!selectedPasajero) {
        showSnackbar("Debe seleccionar un pasajero", "warning")
        return
      }

      // Verificar si el pasajero ya está en la lista
      const yaExiste = pasajerosList.some(p => p.idEntidad === selectedPasajero.id)
      if (yaExiste) {
        showSnackbar("Este pasajero ya está agregado al viaje", "warning")
        return
      }

      // Buscar precio para esta entidad + item + ubicación
      let precioListaItem = await getListaMontosByIdItemIdEntidad(
        selectedPasajero.id,
        selectedGrupo.id,
        selectedDestino.id,
      )

      // Si no hay precio para esta entidad, buscar precio del cliente genérico
      if (!precioListaItem || !precioListaItem.monto) {
        console.log("No se encontró precio para esta entidad, buscando precio genérico...")
        precioListaItem = await getListaMontosByIdItemIdEntidad(
          1, // Cliente genérico
          selectedGrupo.id,
          selectedDestino.id,
        )
      }

      const precioPasajero = precioListaItem ? precioListaItem.monto : 0

      if (precioPasajero === 0) {
        showSnackbar("No se encontró precio para este viaje", "error")
        return
      }

      const nuevoPasajero = {
        idEntidad: selectedPasajero.id,
        nombre: selectedPasajero.descripcion,
        apellido: selectedPasajero.apellido,
        dniCuitCuil: selectedPasajero.dniCuitCuil,
        precio: precioPasajero,
        descuento: 0,
      }

      setPasajerosList([...pasajerosList, nuevoPasajero])
      setSelectedPasajero(null)
      setSearchTextPasajero("")
      
      showSnackbar(`Pasajero "${nuevoPasajero.nombre} ${nuevoPasajero.apellido}" agregado`, "success")

      // Enfocar de nuevo el autocomplete
      if (autocompletePasajeroRef.current) {
        setTimeout(() => {
          autocompletePasajeroRef.current.focus()
        }, 100)
      }
    } catch (error) {
      console.error("Error al agregar pasajero:", error)
      showSnackbar("Error al agregar el pasajero", "error")
    }
  }

  // Función para eliminar pasajero
  const handleDeletePasajero = (index) => {
    const pasajeroToRemove = pasajerosList[index]
    setPasajerosList(pasajerosList.filter((_, i) => i !== index))
    showSnackbar(`Pasajero "${pasajeroToRemove.nombre} ${pasajeroToRemove.apellido}" eliminado`, "info")
  }

  // Función para eliminar todos los pasajeros
  const handleDeleteAllPasajeros = () => {
    if (pasajerosList.length === 0) {
      showSnackbar("No hay pasajeros para eliminar", "info")
      return
    }

    setPasajerosList([])
    showSnackbar("Todos los pasajeros han sido eliminados", "info")
  }

  // Función para actualizar descuento de pasajero
  const handleUpdatePasajeroDescuento = (index, newDescuento) => {
    const updatedList = [...pasajerosList]
    updatedList[index].descuento = Number(newDescuento) || 0
    setPasajerosList(updatedList)
  }

  // Función para aplicar descuentos
  const applyDiscounts = () => {
    const valorDesc = Number(Number.parseFloat(tempValorDescuento).toFixed(2)) || 0
    const porcentajeDesc = Number(Number.parseFloat(tempPorcentajeDescuento).toFixed(2)) || 0

    // Si hay descuento en porcentaje, aplicarlo a todos los pasajeros
    if (porcentajeDesc > 0) {
      const updatedList = pasajerosList.map(pasajero => ({
        ...pasajero,
        descuento: porcentajeDesc
      }))
      setPasajerosList(updatedList)
      showSnackbar(`Descuento del ${porcentajeDesc}% aplicado a todos los pasajeros`, "success")
    }
  }

  // Función para abrir modal de crear pasajero
  const handleAbrirModalCrearPasajero = () => {
    setAbrirModalCrearPasajero(true)
    setIsEditing(false)
    setPasajeroAEditar(null)
  }

  // Función para cerrar modal de crear pasajero
  const handleCerrarModalCrearPasajero = () => {
    setAbrirModalCrearPasajero(false)
    setPasajeroAEditar(null)
  }

  // Función para manejar cuando se crea un nuevo pasajero
  const handlePasajeroCreado = (pasajeroData) => {
    // Autocompletar el campo pasajero con el nuevo pasajero creado
    setSelectedPasajero(pasajeroData)
    
    // Actualizar la lista de entidades para que aparezca el nuevo pasajero
    getEntidadesClientesTresLetras("", 1)
    
    console.log("Pasajero creado y autocompletado:", pasajeroData)
    showSnackbar("Pasajero creado exitosamente", "success")
  }

  // Función para manejar alerta de creación/actualización de pasajero
  const handleAlerta = (exito) => {
    if (exito) {
      showSnackbar("Pasajero creado/actualizado correctamente", "success")
    } else {
      showSnackbar("Error al crear/actualizar el pasajero", "error")
    }
  }

  // Función para generar transacciones
  const handleGenerarTransacciones = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      if (!selectedGrupo || !selectedDestino) {
        showSnackbar("Debe seleccionar un grupo y un destino", "error")
        setLoading(false)
        return
      }

      if (pasajerosList.length === 0) {
        showSnackbar("Debe agregar al menos un pasajero", "error")
        setLoading(false)
        return
      }

      if (!usuarioSeleccionado) {
        showSnackbar("Debe seleccionar un usuario", "error")
        setLoading(false)
        return
      }

      // Calcular descuentos de transacción
      const valorDescTransaccion = Number.parseFloat(tempValorDescuento) || 0
      const porcentajeDescTransaccion = Number.parseFloat(tempPorcentajeDescuento) || 0

      let transaccionesCreadas = 0

      // Generar una transacción por cada pasajero
      for (const pasajero of pasajerosList) {
        try {
          // Calcular precio con descuento individual del pasajero
          const precioConDescuentoIndividual = pasajero.precio * (1 - (pasajero.descuento || 0) / 100)

          // Aplicar descuentos de transacción (proporcional)
          const totalBaseTransaccion = pasajerosList.reduce((total, p) => {
            return total + (p.precio * (1 - (p.descuento || 0) / 100))
          }, 0)

          const proporcionPasajero = totalBaseTransaccion > 0 ? precioConDescuentoIndividual / totalBaseTransaccion : 0
          
          const descuentoFijoPasajero = valorDescTransaccion * proporcionPasajero
          const descuentoPorcentualPasajero = precioConDescuentoIndividual * (porcentajeDescTransaccion / 100)

          let precioFinalPasajero = precioConDescuentoIndividual - descuentoFijoPasajero - descuentoPorcentualPasajero
          
          if (precioFinalPasajero < 0) {
            precioFinalPasajero = 0
          }

          // Crear transacción
          const descripcionTransaccion = `Viaje: ${selectedGrupo.descripcion} - Destino: ${selectedDestino.label} - Pasajero: ${pasajero.nombre} ${pasajero.apellido}`

          const transaccionResponse = await postTransaccion(
            precioFinalPasajero,
            1, // Tipo transacción: venta
            pasajero.idEntidad,
            null,
            descripcionTransaccion,
            usuarioSeleccionado.id,
            selectedDestino.id,
            1, // Negocio por defecto
            0, // Monto descuento (ya aplicado en el precio)
            0, // Porcentaje descuento (ya aplicado en el precio)
          )

          const idTransaccion = transaccionResponse.data.id

          // Crear TransaccionItem
          await postTransaccionItem(
            idTransaccion,
            selectedGrupo.id,
            1, // Cantidad: 1 pasajero
            precioFinalPasajero,
            pasajero.descuento || 0
          )

          transaccionesCreadas++
        } catch (error) {
          console.error(`Error al crear transacción para ${pasajero.nombre}:`, error)
          showSnackbar(`Error al crear transacción para ${pasajero.nombre} ${pasajero.apellido}`, "error")
        }
      }

      // Resetear formulario
      setPasajerosList([])
      setSelectedGrupo(null)
      setSelectedDestino(null)
      setSearchTextGrupo("")
      setTempValorDescuento("0")
      setTempPorcentajeDescuento("0")
      setAplicarDescuento(false)

      showSnackbar(`${transaccionesCreadas} transacción(es) generada(s) exitosamente`, "success")
      
    } catch (error) {
      console.error("Error al generar las transacciones:", error)
      showSnackbar("Error al generar las transacciones", "error")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "#f5f5f5",
        position: "relative",
      }}
    >
      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          p: 2,
          overflow: "auto",
        }}
      >
        <form onSubmit={handleGenerarTransacciones}>
          <Grid container spacing={2}>
            {/* Left Column - Grupo, Destino y Pasajeros */}
            <Grid item xs={12} md={9}>
              {/* Card para seleccionar Grupo y Destino */}
              <Card elevation={3} sx={{ mb: 2, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                    <LuggageIcon sx={{ mr: 1, color: "rgb(244, 147, 14)" }} />
                    Configurar Viaje
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        size="small"
                        fullWidth
                        disablePortal
                        options={(itemTresLetras || []).map((it) => ({
                          label: `${it.codigo} - ${it.descripcion}`,
                          descripcion: it.descripcion,
                          codigo: it.codigo,
                          id: it.id,
                        }))}
                        value={selectedGrupo}
                        onChange={(event, newValue) => setSelectedGrupo(newValue)}
                        onInputChange={(event, newInputValue) => setSearchTextGrupo(newInputValue)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Buscar Grupo/Paquete"
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  <GroupIcon color="action" sx={{ ml: 1, mr: 0.5 }} />
                                  {params.InputProps.startAdornment}
                                </>
                              ),
                            }}
                            inputRef={autocompleteGrupoRef}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        size="small"
                        fullWidth
                        disablePortal
                        options={ubicaciones.map((it) => ({
                          id: it.id,
                          label: it.descripcion,
                        }))}
                        value={selectedDestino}
                        onChange={(event, newValue) => setSelectedDestino(newValue)}
                        disabled={!selectedGrupo}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Destino"
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  <LocationOnIcon color="action" sx={{ ml: 1, mr: 0.5 }} />
                                  {params.InputProps.startAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>

                  {selectedGrupo && selectedDestino && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      Viaje configurado: <strong>{selectedGrupo.descripcion}</strong> con destino a <strong>{selectedDestino.label}</strong>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Card para agregar pasajeros */}
              <Card elevation={3} sx={{ mb: 2, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                    <FlightTakeoffIcon sx={{ mr: 1, color: "rgb(244, 147, 14)" }} />
                    Agregar Pasajeros
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "stretch", sm: "center" },
                      gap: 1.5,
                    }}
                  >
                    <Autocomplete
                      size="small"
                      fullWidth
                      disablePortal
                      options={entidadesClientesTresLetras.map((it) => ({
                        label: `${it.descripcion} ${it.apellido} - ${it.dniCuitCuil}`,
                        descripcion: it.descripcion,
                        apellido: it.apellido,
                        dniCuitCuil: it.dniCuitCuil,
                        id: it.id,
                        telefono: it.telefono,
                        email: it.email,
                        direccion: it.direccion,
                        localidad: it.localidad,
                        provincia: it.provincia,
                        idTipoEntidad: it.idTipoEntidad,
                      }))}
                      value={selectedPasajero}
                      onChange={(event, newValue) => setSelectedPasajero(newValue)}
                      onInputChange={(event, newInputValue) => setSearchTextPasajero(newInputValue)}
                      disabled={!selectedGrupo || !selectedDestino}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Buscar Pasajero"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <SearchIcon color="action" sx={{ ml: 1, mr: 0.5 }} />
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                          inputRef={autocompletePasajeroRef}
                        />
                      )}
                    />

                    <Tooltip title="Crear Nuevo Pasajero">
                      <IconButton 
                        color="primary" 
                        onClick={handleAbrirModalCrearPasajero}
                        disabled={!selectedGrupo || !selectedDestino}
                      >
                        <PersonAddAltIcon />
                      </IconButton>
                    </Tooltip>

                    <Button
                      variant="contained"
                      onClick={handleAddPasajero}
                      startIcon={<AddCircleOutlineIcon />}
                      disabled={!selectedPasajero || !selectedGrupo || !selectedDestino}
                      sx={{ 
                        backgroundColor: "#5b5b5f", 
                        color: "white",
                        minWidth: { xs: "100%", sm: "auto" }
                      }}
                    >
                      Agregar
                    </Button>
                  </Box>
                </CardContent>
              </Card>

              {/* Tabla de pasajeros */}
              <Card elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
                <TableContainer
                  sx={{
                    maxHeight: {
                      xs: "calc(100vh - 400px)",
                      md: "calc(100vh - 350px)",
                    },
                    overflowX: "auto",
                    overflowY: "auto",
                  }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Pasajero</StyledTableCell>
                        <StyledTableCell align="right">DNI/CUIT</StyledTableCell>
                        <StyledTableCell align="right">Precio Base</StyledTableCell>
                        <StyledTableCell align="right">Descuento</StyledTableCell>
                        <StyledTableCell align="right">Total</StyledTableCell>
                        <StyledTableCell align="center">Acciones</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pasajerosList.length > 0 ? (
                        pasajerosList.map((pasajero, index) => {
                          const precioConDescuento = pasajero.precio * (1 - (pasajero.descuento || 0) / 100)
                          
                          return (
                            <StyledTableRow key={index}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {pasajero.nombre} {pasajero.apellido}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">
                                  {pasajero.dniCuitCuil}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">
                                  ${formatNumberForDisplay(pasajero.precio, { locale: localeConfig.locale })}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <TextField
                                  value={formatInputWithSeparators(pasajero.descuento || 0, localeConfig.locale)}
                                  size="small"
                                  onChange={(e) => {
                                    const parsed = parseLocalizedNumber(e.target.value, localeConfig.locale)
                                    const newValue = parsed !== null ? parsed : 0
                                    const updatedList = [...pasajerosList]
                                    updatedList[index] = {
                                      ...updatedList[index],
                                      descuento: newValue
                                    }
                                    setPasajerosList(updatedList)
                                  }}
                                  onBlur={(e) => {
                                    const parsed = parseLocalizedNumber(e.target.value, localeConfig.locale)
                                    handleUpdatePasajeroDescuento(index, parsed !== null ? parsed : 0)
                                  }}
                                  sx={{
                                    width: 80,
                                    "& .MuiInputBase-root": {
                                      borderRadius: 1,
                                      height: 32,
                                    },
                                  }}
                                  InputProps={{ endAdornment: <span>%</span> }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Typography fontWeight="medium">
                                  ${formatNumberForDisplay(precioConDescuento, { locale: localeConfig.locale })}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title="Eliminar pasajero">
                                  <IconButton 
                                    color="error" 
                                    size="small" 
                                    onClick={() => handleDeletePasajero(index)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </StyledTableRow>
                          )
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                            <Typography variant="body1" color="text.secondary">
                              No hay pasajeros agregados
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              {!selectedGrupo || !selectedDestino 
                                ? "Seleccione un grupo y destino para agregar pasajeros"
                                : "Busque y agregue pasajeros para el viaje"
                              }
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell colSpan={6} align="right">
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={handleDeleteAllPasajeros}
                            disabled={pasajerosList.length === 0}
                            sx={{ mt: 1, mb: 1 }}
                          >
                            Eliminar todos
                          </Button>
                        </TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              </Card>
            </Grid>

            {/* Right Column - Usuario y Resumen */}
            <Grid item xs={12} md={3}>
              {/* Card de Usuario */}
              <Card elevation={3} sx={{ mb: 2, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                    <PersonIcon sx={{ mr: 1, color: "rgb(244, 147, 14)" }} />
                    Usuario
                  </Typography>
                  <Autocomplete
                    size="small"
                    fullWidth
                    disablePortal
                    options={usuarios.map((it) => ({
                      label: `${it.usuario} ${it.nombre || ""} ${it.apellido || ""}`,
                      nombre: it.nombre,
                      apellido: it.apellido,
                      id: it.id,
                      usuario: it.usuario,
                    }))}
                    value={usuarioSeleccionado}
                    onChange={(event, newValue) => setUsuarioSeleccionado(newValue)}
                    renderInput={(params) => <TextField {...params} label="Usuario" />}
                  />
                </CardContent>
              </Card>

              {/* Resumen del Viaje */}
              <Card elevation={3} sx={{ mb: 2, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                    <ReceiptIcon sx={{ mr: 1, color: "rgb(244, 147, 14)" }} />
                    Resumen del Viaje
                  </Typography>

                  <Box
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.light, 0.1),
                      p: 2,
                      borderRadius: 1,
                      mb: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body2">Grupo:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {selectedGrupo ? selectedGrupo.descripcion : "-"}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body2">Destino:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {selectedDestino ? selectedDestino.label : "-"}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                      <Typography variant="body2">Pasajeros:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {pasajerosList.length}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body1">Subtotal:</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        ${formatNumberForDisplay(precioBaseTotal, { locale: localeConfig.locale })}
                      </Typography>
                    </Box>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={aplicarDescuento}
                          onChange={(e) => setAplicarDescuento(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Aplicar Descuento"
                    />

                    <Collapse in={aplicarDescuento} timeout={300}>
                      <Box sx={{ mt: 1, mb: 1 }}>
                        <TextField
                          label="Descuento en Valor ($)"
                          value={formatInputWithSeparators(tempValorDescuento || 0, localeConfig.locale)}
                          onChange={(e) => {
                            const parsed = parseLocalizedNumber(e.target.value, localeConfig.locale)
                            setTempValorDescuento(parsed !== null ? parsed.toString() : "0")
                          }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                          fullWidth
                          size="small"
                          sx={{ mb: 1 }}
                        />
                        <TextField
                          label="Descuento en Porcentaje (%)"
                          value={formatInputWithSeparators(tempPorcentajeDescuento || 0, localeConfig.locale)}
                          onChange={(e) => {
                            const parsed = parseLocalizedNumber(e.target.value, localeConfig.locale)
                            setTempPorcentajeDescuento(parsed !== null ? parsed.toString() : "0")
                          }}
                          onBlur={applyDiscounts}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                          fullWidth
                          size="small"
                        />
                      </Box>
                    </Collapse>

                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                      <Typography variant="h6">TOTAL:</Typography>
                      <Typography variant="h6" color="rgb(244, 147, 14)" fontWeight="bold">
                        ${formatNumberForDisplay(precioTotal, { locale: localeConfig.locale })}
                      </Typography>
                    </Box>
                  </Box>

                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    fullWidth
                    size="large"
                    disabled={isButtonDisabled}
                    startIcon={<SendIcon />}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: "bold",
                      boxShadow: 3,
                      backgroundColor: "green",
                      color: "white",
                    }}
                  >
                    GENERAR TRANSACCIONES
                  </Button>

                  {pasajerosList.length > 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Se generarán {pasajerosList.length} transacción(es)
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </form>
      </Box>

      {/* Modal para crear pasajero */}
      <CrearEntidadClienteModalComponent
        open={abrirModalCrearPasajero}
        handleClose={handleCerrarModalCrearPasajero}
        entidad={pasajeroAEditar}
        isEditing={isEditing}
        handleAlerta={handleAlerta}
        onClienteCreado={handlePasajeroCreado}
        tipoEntidad="cliente"
      />

      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              textAlign: "center",
              p: 3,
              borderRadius: 2,
              bgcolor: "background.paper",
            }}
          >
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6">Procesando...</Typography>
          </Box>
        </Box>
      )}

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default GestionGenerarVentaComponent
